///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, OnDestroy, OnInit } from '@angular/core';
import { GtthHistoryService } from '../engine/history-service';
import { Subject, Subscription } from 'rxjs';
import { GtthPosition } from '../entities/position';
import { GtthStepGroupEnum, TopologyAltitudeModeEnum } from '../../../generated/shared-types.model';
import { NodeFeature } from '../entities/node/node-feature';
import { GtthModifyUnitInfoCommand } from '../engine/commands/node/modify-unit-info-command';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { GtthEventManager } from '../../../services/event-manager.service';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { GtthEntityNameValidation } from '../../../shared/directives/validation-functions/entity-name';
import { feetMultiplier } from '../../../shared/utils';
import { SuiSvgIconComponent } from '@siklu/sui-lib';
import { NgClass } from '@angular/common';
import { ButtonsDirectivesModule } from '../../../shared/directives/buttons-directives/buttons-directives.module';

@Component({
    selector: 'winde-unit-config',
    templateUrl: './unit-config.component.html',
    styleUrls: ['entities-config-panel.scss'],
    imports: [ReactiveFormsModule, NgClass, SuiSvgIconComponent, ButtonsDirectivesModule]
})
export class GtthUnitConfigComponent implements OnInit, OnDestroy {
    public editForm: FormGroup;

    altitudeModeEnumValues = TopologyAltitudeModeEnum;

    station: NodeFeature;
    moveSubject = new Subject();
    isDraggable = false;
    hasFreezeLink: boolean;
    private modifyConfigCommand: GtthModifyUnitInfoCommand;
    private lng: number;
    private lat: number;
    private entitiesNames: string[];
    private isLocationsAndLosStep = false;
    private fieldsValChangeSubscription: Subscription[];
    private readonly destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        public readonly historyService: GtthHistoryService,
        private readonly eventManager: GtthEventManager,
        private readonly formBuilder: FormBuilder
    ) {}

    private _height: number;

    get height(): number {
        return this._height;
    }

    set height(value: number) {
        this._height = value;
        if (this.modifyConfigCommand) {
            this.modifyConfigCommand.setHeight(this._height);
        }
    }

    private _heightInUnits: number;

    get heightInUnits(): number {
        return this._heightInUnits;
    }

    set heightInUnits(value: number) {
        this._heightInUnits = value;
        this.height =
            this.historyService.logicalTopology.productUnits === 'feet'
                ? Math.round(this._heightInUnits * feetMultiplier)
                : this._heightInUnits;
    }

    private _alt_mode: TopologyAltitudeModeEnum;

    get alt_mode(): TopologyAltitudeModeEnum {
        return this._alt_mode;
    }

    set alt_mode(value: TopologyAltitudeModeEnum) {
        this._alt_mode = value;
        if (this.modifyConfigCommand) {
            this.modifyConfigCommand.setAltitudeMode(this._alt_mode);
        }
    }

    private _name: string;

    get name(): string {
        return this._name;
    }

    set name(value: string) {
        this._name = value;
        if (this.modifyConfigCommand) {
            this.modifyConfigCommand.setName(this._name);
        }
    }

    ngOnInit() {
        this.eventManager
            .getEventSubscription('unitConfigShow')
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => this.showPanel(response.entityId, response.modifyCommand));
        this.eventManager
            .getEventSubscription('updateConfigShow')
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => this.updateCommand(response.entityId, response.modifyCommand));
        this.eventManager
            .getEventSubscription('unitConfigHide')
            .pipe(takeUntil(this.destroy$))
            .subscribe((_response) => this.closePanel());
        this.eventManager
            .getEventSubscription(GtthEventManager.NODE_MOVE_EVENT)
            .pipe(takeUntil(this.destroy$))
            .subscribe((event) => {
                if (this.station && this.station.getId() === event.id) {
                    this.editForm.get('lng').setValue(this.station.getPosition().longitude);
                    this.editForm.get('lat').setValue(this.station.getPosition().latitude);
                }
            });

        this.moveSubject
            .asObservable()
            .pipe(debounceTime(500))
            .subscribe(() => {
                if (this.modifyConfigCommand) {
                    this.modifyConfigCommand.setPosition(GtthPosition.fromValues(this.lng, this.lat));
                }
            });
    }

    initForm() {
        this.editForm = this.formBuilder.group({
            name: [
                {
                    value: this.name,
                    disabled: this.isLocationsAndLosStep
                },
                Validators.compose([
                    Validators.maxLength(70),
                    GtthEntityNameValidation(/[^\-a-zA-Z0-9_!@#$%^&()[\]+={}~'`.]/g),
                    this.validateName()
                ])
            ],
            lng: [
                {
                    value: this.lng,
                    disabled: !this.isDraggable || this.isLocationsAndLosStep
                },
                Validators.compose([Validators.min(-180), Validators.max(180)])
            ],
            lat: [
                {
                    value: this.lat,
                    disabled: !this.isDraggable || this.isLocationsAndLosStep
                },
                Validators.compose([Validators.min(-90), Validators.max(90)])
            ],
            height: [
                {
                    value: this.heightInUnits,
                    disabled: this.isLocationsAndLosStep
                },
                Validators.compose([Validators.min(0)])
            ],
            alt_mode: [
                {
                    value: this.alt_mode,
                    disabled: this.isLocationsAndLosStep
                }
            ]
        });

        this.fieldsValChangeSubscription = [
            this.editForm
                .get('name')
                .valueChanges.pipe(takeUntil(this.destroy$))
                .subscribe((val) => (this.name = val)),
            this.editForm
                .get('lng')
                .valueChanges.pipe(takeUntil(this.destroy$))
                .subscribe(() => this.positionChanged()),
            this.editForm
                .get('lat')
                .valueChanges.pipe(takeUntil(this.destroy$))
                .subscribe(() => this.positionChanged()),
            this.editForm
                .get('height')
                .valueChanges.pipe(takeUntil(this.destroy$))
                .subscribe((val) => (this.heightInUnits = val)),
            this.editForm
                .get('alt_mode')
                .valueChanges.pipe(takeUntil(this.destroy$))
                .subscribe((val) => (this.alt_mode = val))
        ];
    }

    public positionChanged() {
        this.lng = this.editForm.get('lng').value;
        this.lat = this.editForm.get('lat').value;

        if (this.lng < -180) {
            this.lng = -180;
            this.editForm.get('lng').setValue(this.lng, { emitEvent: false });
        } else if (this.lng > 180) {
            this.lng = 180;
            this.editForm.get('lng').setValue(this.lng, { emitEvent: false });
        }

        if (this.lat < -90) {
            this.lat = -90;
            this.editForm.get('lat').setValue(this.lat, { emitEvent: false });
        } else if (this.lat > 90) {
            this.lat = 90;
            this.editForm.get('lat').setValue(this.lat, { emitEvent: false });
        }

        this.moveSubject.next(undefined);
    }

    validateName() {
        return (control: AbstractControl): Record<string, any> | null => {
            if (!control.value) {
                return null;
            }
            return this.entitiesNames.indexOf(control.value) === -1 && control.value !== '' ? null : { "Name isn't unique": true };
        };
    }

    public closePanel() {
        if (this?.editForm?.invalid || (this.modifyConfigCommand && !this.modifyConfigCommand.isValid())) {
            return;
        }
        if (this.fieldsValChangeSubscription) {
            this.fieldsValChangeSubscription.forEach((subs) => {
                subs.unsubscribe();
            });
            this.fieldsValChangeSubscription = null;
        }
        if (this.station) {
            this.station = undefined;
        }
        if (this.modifyConfigCommand) {
            this.modifyConfigCommand = undefined;
        }
    }

    ngOnDestroy() {
        if (this.moveSubject) {
            this.moveSubject.complete();
        }

        this.destroy$.next(true);
        this.destroy$.unsubscribe();
        this.fieldsValChangeSubscription = null;
    }

    private showPanel(entityId: number, modifyCommand: GtthModifyUnitInfoCommand) {
        this.station = this.historyService.logicalTopology.getStation(entityId);
        this.hasFreezeLink = this.station.hasFreezeLinks();

        this.isLocationsAndLosStep = this.historyService.selectedStepNumber !== GtthStepGroupEnum.LOCATIONS_AND_LOSES;

        this.modifyConfigCommand = modifyCommand;
        this.height = this.modifyConfigCommand.getHeight() || this.station.getHeight();
        this.heightInUnits =
            this.historyService.logicalTopology.productUnits === 'feet' ? Math.round(this.height / feetMultiplier) : this.height;
        this.alt_mode = this.modifyConfigCommand.getAltitudeMode() || this.station.getAltitudeMode();

        this.name = this.modifyConfigCommand.getName() || this.station.getName();
        this.lng = this.station.getPosition().longitude;
        this.lat = this.station.getPosition().latitude;

        this.entitiesNames = this.historyService.logicalTopology.getAllStationsNames().filter((name) => name !== this.station.getName());

        this.modifyConfigCommand.setOldHeight(this.station.getHeight());
        this.modifyConfigCommand.setOldAltitudeMode(this.station.getAltitudeMode());

        this.modifyConfigCommand.setOldPosition(this.station.getPosition());

        this.isDraggable = !this.historyService.logicalTopology.hasLinks(entityId);

        this.initForm();
    }

    private updateCommand(entityId: number, modifyCommand: GtthModifyUnitInfoCommand) {
        if (this.station && this.station.getId() === entityId) {
            this.showPanel(entityId, modifyCommand);
        }
    }
}
